import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { QuestionCombobox } from '@/features/quote-and-apply-builder';
import { Plus, X } from 'lucide-react';
import {
  OptionsEditor,
  TableColumnsEditor,
  useQuestionSettings,
  useFormQuestions,
} from '@/features/quote-and-apply-builder';

interface QuestionSettingsDialogProps {
  open: boolean;
  question: PolicyFormQuestion;
  allQuestions: Record<string, PolicyFormQuestion>;
  isNew?: boolean;
  onSave: (updates: Partial<PolicyFormQuestion>) => void;
  onClose: () => void;
}

export function QuestionSettingsDialog({
  open,
  question,
  allQuestions,
  isNew,
  onSave,
  onClose,
}: QuestionSettingsDialogProps) {
  const storeQuestions = useFormQuestions();
  const {
    title,
    setTitle,
    questionRef,
    setQuestionRef,
    questionRefError,
    type,
    required,
    setRequired,
    placeholder,
    setPlaceholder,
    helperText,
    setHelperText,
    validationType,
    setValidationType,
    titleOverride,
    setTitleOverride,
    conditionalTitle,
    setConditionalTitle,
    conditionalHelperText,
    setConditionalHelperText,
    format,
    setFormat,
    disabled,
    setDisabled,
    hidden,
    setHidden,
    overflow,
    setOverflow,
    gridColumns,
    setGridColumns,
    characterLimit,
    setCharacterLimit,
    options,
    setOptions,
    hasOptions,
    columns,
    setColumns,
    minRows,
    setMinRows,
    maxRows,
    setMaxRows,
    hasComposite,
    setHasComposite,
    compositeQuestionRefs,
    compositeDelimiter,
    setCompositeDelimiter,
    compositeIncludeMetadata,
    setCompositeIncludeMetadata,
    compositeAllowAppendText,
    setCompositeAllowAppendText,
    compositeFieldRef,
    setCompositeFieldRef,
    compositeTitle,
    setCompositeTitle,
    compositeMapping,
    hasCompositeCondition,
    setHasCompositeCondition,
    compositeConditionType,
    setCompositeConditionType,
    compositeSimpleCondition,
    setCompositeSimpleCondition,
    compositeLogicalCondition,
    setCompositeLogicalCondition,
    supportsComposite,
    hasVisibleCondition,
    setHasVisibleCondition,
    hasRequiredCondition,
    setHasRequiredCondition,
    hasShowConditionalTextCondition,
    setHasShowConditionalTextCondition,
    visibleConditionType,
    setVisibleConditionType,
    visibleSimpleCondition,
    setVisibleSimpleCondition,
    visibleLogicalCondition,
    setVisibleLogicalCondition,
    requiredConditionType,
    setRequiredConditionType,
    requiredSimpleCondition,
    setRequiredSimpleCondition,
    requiredLogicalCondition,
    setRequiredLogicalCondition,
    showTextConditionType,
    setShowTextConditionType,
    showTextSimpleCondition,
    setShowTextSimpleCondition,
    showTextLogicalCondition,
    setShowTextLogicalCondition,
    questionOptions,
    questionTypes,
    validationTypes,
    comparisonOperators,
    compositeDelimiters,
    handleTitleChange,
    handleQuestionRefChange,
    handleTypeChange,
    handleSave,
    handleAddCompositeQuestionRef,
    handleRemoveCompositeQuestionRef,
    handleUpdateMapping,
  } = useQuestionSettings({ question, allQuestions, isNew, onSave, onClose });

  const renderConditionBuilder = (
    conditionType: 'simple' | 'logical',
    setConditionType: (condType: 'simple' | 'logical') => void,
    simpleCondition: FieldCondition,
    setSimpleCondition: (cond: FieldCondition) => void,
    logicalCondition: LogicalCondition,
    setLogicalCondition: (cond: LogicalCondition) => void,
  ) => {
    const selectedQuestion = storeQuestions[simpleCondition.field];
    const showValueInput = !['empty', 'notEmpty'].includes(simpleCondition.operator);

    const handleAddLogicalCondition = () => {
      setLogicalCondition({
        ...logicalCondition,
        conditions: [
          ...logicalCondition.conditions,
          { field: '', operator: 'equals', value: '' } as FieldCondition,
        ],
      });
    };

    const handleUpdateLogicalCondition = (index: number, updates: Partial<FieldCondition>) => {
      setLogicalCondition({
        ...logicalCondition,
        conditions: logicalCondition.conditions.map((condition, conditionIndex) =>
          conditionIndex === index ? { ...condition, ...updates } : condition,
        ) as (FieldCondition | LogicalCondition)[],
      });
    };

    const handleRemoveLogicalCondition = (index: number) => {
      setLogicalCondition({
        ...logicalCondition,
        conditions: logicalCondition.conditions.filter(
          (_, conditionIndex) => conditionIndex !== index,
        ),
      });
    };

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Rule Type</Label>
          <Select
            value={conditionType}
            onValueChange={value => setConditionType(value as 'simple' | 'logical')}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="simple">Single Rule</SelectItem>
              <SelectItem value="logical">Multiple Rules (AND/OR)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {conditionType === 'simple' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Question</Label>
              <QuestionCombobox
                value={simpleCondition.field}
                options={questionOptions}
                onChange={val =>
                  setSimpleCondition({ ...simpleCondition, field: val as string, value: '' })
                }
                className="w-full"
                placeholder="Select a question..."
              />
            </div>

            <div className="space-y-2">
              <Label>Operator</Label>
              <Select
                value={simpleCondition.operator}
                onValueChange={operator =>
                  setSimpleCondition({
                    ...simpleCondition,
                    operator: operator as ComparisonOperator,
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {comparisonOperators.map(operator => (
                    <SelectItem
                      key={operator.value}
                      value={operator.value}
                    >
                      {operator.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showValueInput && (
              <div className="space-y-2">
                <Label>Value</Label>
                {selectedQuestion?.type === 'select' || selectedQuestion?.type === 'radio' ? (
                  <Select
                    value={(simpleCondition.value as string) || ''}
                    onValueChange={val => setSimpleCondition({ ...simpleCondition, value: val })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedQuestion.options?.map(option => (
                        <SelectItem
                          key={option.id}
                          value={option.id}
                        >
                          {option.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={(simpleCondition.value as string) || ''}
                    onChange={event =>
                      setSimpleCondition({ ...simpleCondition, value: event.target.value })
                    }
                    placeholder="Enter value"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {conditionType === 'logical' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Combine Rules With</Label>
              <Select
                value={logicalCondition.operator}
                onValueChange={operator =>
                  setLogicalCondition({
                    ...logicalCondition,
                    operator: operator as 'and' | 'or',
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="and">AND</SelectItem>
                  <SelectItem value="or">OR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              {logicalCondition.conditions.map((condition, index) => {
                if ('conditions' in condition) return null;
                const fieldCondition = condition as FieldCondition;
                const conditionQuestion = storeQuestions[fieldCondition.field];
                const showVal = !['empty', 'notEmpty'].includes(fieldCondition.operator);

                return (
                  <div
                    key={index}
                    className="bg-muted/50 flex items-start gap-2 rounded-lg border p-2"
                  >
                    <div className="flex-1 space-y-2">
                      <QuestionCombobox
                        value={fieldCondition.field}
                        options={questionOptions}
                        onChange={val =>
                          handleUpdateLogicalCondition(index, {
                            field: val as string,
                            value: '',
                          })
                        }
                        placeholder="Select a question..."
                        className="w-full"
                      />
                      <div className="space-y-2">
                        <Select
                          value={fieldCondition.operator}
                          onValueChange={operator =>
                            handleUpdateLogicalCondition(index, {
                              operator: operator as ComparisonOperator,
                            })
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {comparisonOperators.map(operator => (
                              <SelectItem
                                key={operator.value}
                                value={operator.value}
                              >
                                {operator.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {showVal &&
                          (conditionQuestion?.type === 'select' ||
                          conditionQuestion?.type === 'radio' ? (
                            <Select
                              value={(fieldCondition.value as string) || ''}
                              onValueChange={val =>
                                handleUpdateLogicalCondition(index, { value: val })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select value" />
                              </SelectTrigger>
                              <SelectContent>
                                {conditionQuestion.options?.map(option => (
                                  <SelectItem
                                    key={option.id}
                                    value={option.id}
                                  >
                                    {option.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={(fieldCondition.value as string) || ''}
                              onChange={event =>
                                handleUpdateLogicalCondition(index, {
                                  value: event.target.value,
                                })
                              }
                              placeholder="Value"
                              className="w-full"
                            />
                          ))}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveLogicalCondition(index)}
                    >
                      <X />
                    </Button>
                  </div>
                );
              })}
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleAddLogicalCondition}
            >
              <Plus />
              Add Condition
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="max-h-[95vh] gap-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Question Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic">
          <TabsList className="w-full">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            {hasOptions && <TabsTrigger value="options">Options</TabsTrigger>}
            {type === 'table' && <TabsTrigger value="table">Table</TabsTrigger>}
            {supportsComposite && <TabsTrigger value="composite">Combined Field</TabsTrigger>}
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
            <TabsTrigger value="conditions">Conditions</TabsTrigger>
          </TabsList>

          {/* Basic Tab */}
          <TabsContent value="basic">
            <div className="space-y-6 pt-4">
              <div className="space-y-2">
                <Label htmlFor="question-label">Question Label</Label>
                <Input
                  value={title}
                  onChange={event => handleTitleChange(event.target.value)}
                  placeholder="Enter question label"
                  id="question-label"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="question-id">Question ID</Label>
                <Input
                  value={questionRef}
                  onChange={event => handleQuestionRefChange(event.target.value)}
                  placeholder="e.g., applicant_first_name"
                  id="question-id"
                  className={questionRefError ? 'border-red-500' : ''}
                />
                {questionRefError ? (
                  <p className="text-xs text-red-500">{questionRefError}</p>
                ) : (
                  <p className="text-xs text-gray-500">
                    Unique identifier used to store and reference this question's answer
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="question-type">Question Type</Label>
                <Select
                  value={type}
                  onValueChange={value => handleTypeChange(value as PolicyFormQuestion['type'])}
                >
                  <SelectTrigger
                    id="question-type"
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {questionTypes.map(questionType => (
                      <SelectItem
                        key={questionType.value}
                        value={questionType.value}
                      >
                        {questionType.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="question-placeholder">Placeholder</Label>
                <Input
                  value={placeholder}
                  onChange={event => setPlaceholder(event.target.value)}
                  placeholder="Enter placeholder text"
                  id="question-placeholder"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="question-helper">Helper Text</Label>
                <Textarea
                  value={helperText}
                  onChange={event => setHelperText(event.target.value)}
                  placeholder="Additional instructions for this field"
                  id="question-helper"
                />
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="required"
                  checked={required}
                  onCheckedChange={checked => setRequired(!!checked)}
                />
                <Label htmlFor="required">Required Field</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="answer-format">Answer Format</Label>
                <Select
                  value={validationType || 'none'}
                  onValueChange={value =>
                    setValidationType(
                      value === 'none'
                        ? undefined
                        : (value as PolicyFormQuestion['validationType']),
                    )
                  }
                >
                  <SelectTrigger
                    id="answer-format"
                    className="w-full"
                  >
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {validationTypes.map(validation => (
                      <SelectItem
                        key={validation.value}
                        value={validation.value}
                      >
                        {validation.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          {/* Options Tab */}
          <TabsContent value="options">
            <div className="pt-4">
              {hasOptions ? (
                <div className="space-y-2">
                  <Label>Options</Label>
                  <OptionsEditor
                    options={options}
                    allQuestions={allQuestions}
                    onChange={setOptions}
                  />
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-gray-500">
                  Options are only available for Dropdown, Single Choice, Multiple Choice, and Short
                  Text + Dropdown question types.
                </p>
              )}
            </div>
          </TabsContent>

          {/* Table Tab */}
          <TabsContent value="table">
            <div className="space-y-6 pt-4">
              {type === 'table' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="min-rows">Minimum Rows</Label>
                      <Input
                        type="number"
                        value={minRows || ''}
                        onChange={event =>
                          setMinRows(event.target.value ? parseInt(event.target.value) : undefined)
                        }
                        placeholder="e.g., 1"
                        min={0}
                        id="min-rows"
                      />
                      <p className="text-xs text-gray-500">Minimum number of rows required</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-rows">Maximum Rows</Label>
                      <Input
                        type="number"
                        value={maxRows || ''}
                        onChange={event =>
                          setMaxRows(event.target.value ? parseInt(event.target.value) : undefined)
                        }
                        placeholder="e.g., 10"
                        min={1}
                        id="max-rows"
                      />
                      <p className="text-xs text-gray-500">Maximum number of rows allowed</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Table Columns</Label>
                    <TableColumnsEditor
                      columns={columns}
                      onChange={setColumns}
                    />
                  </div>
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-gray-500">
                  Table configuration is only available for Table field type.
                </p>
              )}
            </div>
          </TabsContent>

          {/* Composite Tab */}
          <TabsContent value="composite">
            <div className="space-y-6 pt-4">
              <div className="space-y-2">
                <Label htmlFor="enable-composite">Enable Combined Field</Label>
                <p className="text-xs text-gray-500">
                  Merge answers from multiple questions into one field
                </p>
                <Select
                  value={hasComposite ? 'yes' : 'no'}
                  onValueChange={value => setHasComposite(value === 'yes')}
                >
                  <SelectTrigger
                    id="enable-composite"
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">Disabled</SelectItem>
                    <SelectItem value="yes">Enabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {hasComposite && (
                <div className="space-y-6 rounded-lg border p-4">
                  <div className="space-y-2">
                    <Label>Questions to Combine</Label>
                    <p className="text-xs text-gray-500">
                      Choose which questions' answers to merge
                    </p>

                    <div className="space-y-2">
                      {compositeQuestionRefs.map((ref, index) => {
                        const refQuestion = allQuestions[ref];
                        const isRadioType = refQuestion?.type === 'radio';
                        return (
                          <div
                            key={ref}
                            className="rounded-lg border px-3 py-2"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-mono text-sm text-gray-950">{ref}</span>
                                {refQuestion && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    ({refQuestion.title})
                                  </span>
                                )}
                                {isRadioType && (
                                  <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                                    Radio
                                  </span>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveCompositeQuestionRef(index)}
                              >
                                <X />
                              </Button>
                            </div>

                            {isRadioType &&
                              refQuestion.options &&
                              refQuestion.options.length > 0 && (
                                <div className="mt-2 space-y-1.5 border-t pt-2">
                                  <Label>Answer Display Mapping</Label>
                                  <div className="grid gap-1.5">
                                    {refQuestion.options.map(option => (
                                      <div
                                        key={option.id}
                                        className="flex items-center gap-2"
                                      >
                                        <span
                                          className="w-24 truncate text-xs text-gray-600"
                                          title={option.value}
                                        >
                                          {option.value}
                                        </span>
                                        <span className="text-xs text-gray-400">&rarr;</span>
                                        <Input
                                          value={compositeMapping[`${ref}:${option.id}`] || ''}
                                          onChange={event =>
                                            handleUpdateMapping(
                                              `${ref}:${option.id}`,
                                              event.target.value,
                                            )
                                          }
                                          placeholder={option.id}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>

                    <QuestionCombobox
                      value=""
                      options={questionOptions.filter(
                        option => !compositeQuestionRefs.includes(option.value),
                      )}
                      onChange={val => handleAddCompositeQuestionRef(val as string)}
                      placeholder="Add a question..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="composite-delimiter">Delimiter</Label>
                    <Select
                      value={compositeDelimiter}
                      onValueChange={value => setCompositeDelimiter(value as CompositeDelimiter)}
                    >
                      <SelectTrigger
                        id="composite-delimiter"
                        className="w-full"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {compositeDelimiters.map(delimiter => (
                          <SelectItem
                            key={delimiter.value}
                            value={delimiter.value}
                          >
                            {delimiter.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="composite-field-id">Combined Field ID</Label>
                      <Input
                        value={compositeFieldRef}
                        onChange={event => setCompositeFieldRef(event.target.value)}
                        placeholder="e.g., full_address"
                        id="composite-field-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="composite-field-label">Combined Field Label</Label>
                      <Input
                        value={compositeTitle}
                        onChange={event => setCompositeTitle(event.target.value)}
                        placeholder="e.g., Full Address"
                        id="composite-field-label"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-6">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="includeMetadata"
                        checked={compositeIncludeMetadata}
                        onCheckedChange={checked => setCompositeIncludeMetadata(!!checked)}
                      />
                      <Label htmlFor="includeMetadata">Include Additional Info</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="allowAppendText"
                        checked={compositeAllowAppendText}
                        onCheckedChange={checked => setCompositeAllowAppendText(!!checked)}
                      />
                      <Label htmlFor="allowAppendText">Allow Adding Extra Text</Label>
                    </div>
                  </div>

                  <div className="space-y-6 border-t pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="combine-condition">Combine Condition</Label>
                      <Select
                        value={hasCompositeCondition ? 'yes' : 'no'}
                        onValueChange={value => setHasCompositeCondition(value === 'yes')}
                      >
                        <SelectTrigger
                          id="combine-condition"
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">Always Apply</SelectItem>
                          <SelectItem value="yes">Conditional</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        When set, answers are only combined when this condition is met
                      </p>
                    </div>

                    {hasCompositeCondition && (
                      <div className="rounded-lg border p-3">
                        {renderConditionBuilder(
                          compositeConditionType,
                          setCompositeConditionType,
                          compositeSimpleCondition,
                          setCompositeSimpleCondition,
                          compositeLogicalCondition,
                          setCompositeLogicalCondition,
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced">
            <div className="space-y-6 pt-4">
              <div className="space-y-2">
                <Label htmlFor="alt-title">Alternative Title</Label>
                <Input
                  value={titleOverride}
                  onChange={event => setTitleOverride(event.target.value)}
                  placeholder="Alternative title for display"
                  id="alt-title"
                />
                <p className="text-xs text-gray-500">
                  Replaces the main title when used in certain views
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="conditional-title">Conditional Title</Label>
                  <Input
                    value={conditionalTitle}
                    onChange={event => setConditionalTitle(event.target.value)}
                    placeholder="Title when condition is met"
                    id="conditional-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="conditional-helper">Conditional Helper Text</Label>
                  <Input
                    value={conditionalHelperText}
                    onChange={event => setConditionalHelperText(event.target.value)}
                    placeholder="Helper text when condition is met"
                    id="conditional-helper"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="option-layout">Option Layout</Label>
                  <Select
                    value={format || 'none'}
                    onValueChange={value =>
                      setFormat(
                        value === 'none' ? undefined : (value as PolicyFormQuestion['format']),
                      )
                    }
                  >
                    <SelectTrigger
                      id="option-layout"
                      className="w-full"
                    >
                      <SelectValue placeholder="Default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Default</SelectItem>
                      <SelectItem value="vertical">Stacked</SelectItem>
                      <SelectItem value="horizontal">Side by Side</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="columns-per-row">Columns Per Row</Label>
                  <Input
                    type="number"
                    value={gridColumns || ''}
                    onChange={event =>
                      setGridColumns(event.target.value ? parseInt(event.target.value) : undefined)
                    }
                    placeholder="e.g., 2"
                    min={1}
                    max={12}
                    id="columns-per-row"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="char-limit">Character Limit</Label>
                  <Input
                    type="number"
                    value={characterLimit?.limit || ''}
                    onChange={event =>
                      setCharacterLimit(
                        event.target.value
                          ? {
                              limit: parseInt(event.target.value),
                              mode: characterLimit?.mode || 'strict',
                            }
                          : undefined,
                      )
                    }
                    placeholder="Max characters"
                    min={1}
                    id="char-limit"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="limit-behavior">When Limit is Reached</Label>
                  <Select
                    value={characterLimit?.mode || 'strict'}
                    onValueChange={value => {
                      if (characterLimit) {
                        setCharacterLimit({
                          ...characterLimit,
                          mode: value as 'strict' | 'overflow',
                        });
                      }
                    }}
                    disabled={!characterLimit}
                  >
                    <SelectTrigger
                      id="limit-behavior"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strict">Stop typing at limit</SelectItem>
                      <SelectItem value="overflow">Allow typing, show warning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap gap-6 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="disabled"
                    checked={disabled}
                    onCheckedChange={checked => setDisabled(!!checked)}
                  />
                  <Label htmlFor="disabled">Disabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hidden"
                    checked={hidden}
                    onCheckedChange={checked => setHidden(!!checked)}
                  />
                  <Label htmlFor="hidden">Hidden</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="overflow"
                    checked={overflow}
                    onCheckedChange={checked => setOverflow(!!checked)}
                  />
                  <Label htmlFor="overflow">Allow Overflow</Label>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Conditions Tab */}
          <TabsContent value="conditions">
            <div className="space-y-6 pt-4">
              <div className="space-y-6 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="visible-condition">Show/Hide Rule</Label>
                  <Select
                    value={hasVisibleCondition ? 'yes' : 'no'}
                    onValueChange={value => setHasVisibleCondition(value === 'yes')}
                  >
                    <SelectTrigger
                      id="visible-condition"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">Always Show</SelectItem>
                      <SelectItem value="yes">Conditional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {hasVisibleCondition &&
                  renderConditionBuilder(
                    visibleConditionType,
                    setVisibleConditionType,
                    visibleSimpleCondition,
                    setVisibleSimpleCondition,
                    visibleLogicalCondition,
                    setVisibleLogicalCondition,
                  )}
              </div>

              <div className="space-y-6 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="required-condition">Required Condition</Label>
                  <Select
                    value={hasRequiredCondition ? 'yes' : 'no'}
                    onValueChange={value => setHasRequiredCondition(value === 'yes')}
                  >
                    <SelectTrigger
                      id="required-condition"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">Always Use Required Setting</SelectItem>
                      <SelectItem value="yes">Conditional</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    When set, the "Required" setting above is ignored and this rule is used instead.
                  </p>
                </div>

                {hasRequiredCondition &&
                  renderConditionBuilder(
                    requiredConditionType,
                    setRequiredConditionType,
                    requiredSimpleCondition,
                    setRequiredSimpleCondition,
                    requiredLogicalCondition,
                    setRequiredLogicalCondition,
                  )}
              </div>

              <div className="space-y-6 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="alt-text-condition">Show Alternate Text</Label>
                  <Select
                    value={hasShowConditionalTextCondition ? 'yes' : 'no'}
                    onValueChange={value => setHasShowConditionalTextCondition(value === 'yes')}
                  >
                    <SelectTrigger
                      id="alt-text-condition"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">Never</SelectItem>
                      <SelectItem value="yes">Conditional</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    When this condition is met, the alternate title and helper text are shown
                    instead.
                  </p>
                </div>

                {hasShowConditionalTextCondition &&
                  renderConditionBuilder(
                    showTextConditionType,
                    setShowTextConditionType,
                    showTextSimpleCondition,
                    setShowTextSimpleCondition,
                    showTextLogicalCondition,
                    setShowTextLogicalCondition,
                  )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
